import { type UserListItem, type UserSalesHaul, type UserSalesScope } from '@utazzo/shared';
import React from 'react';

import { SoftPill } from '@/components/soft-pill';

export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  SALES_LEAD: 'Sales Lead',
  SALES_AGENT: 'Sales Agent',
  OPS: 'Operations',
  FINANCE: 'Finance',
  HR: 'HR',
  READ_ONLY: 'Read-only',
  CONTENT_EDITOR: 'Content Editor',
  MARKETING_LEAD: 'Marketing Lead',
};

export const ROLE_TONES: Record<string, string> = {
  ADMIN: 'bg-violet-50 text-violet-700',
  SALES_LEAD: 'bg-blue-50 text-blue-700',
  SALES_AGENT: 'bg-sky-50 text-sky-700',
  OPS: 'bg-amber-50 text-amber-700',
  FINANCE: 'bg-emerald-50 text-emerald-700',
  HR: 'bg-rose-50 text-rose-700',
  READ_ONLY: 'bg-zinc-100 text-zinc-600',
  CONTENT_EDITOR: 'bg-slate-100 text-slate-700',
  MARKETING_LEAD: 'bg-indigo-50 text-indigo-700',
};

const SCOPE_LABELS: Record<UserSalesScope, string> = {
  INTERNATIONAL: 'International',
  DOMESTIC: 'Domestic',
};

const HAUL_LABELS: Record<UserSalesHaul, string> = {
  SHORT_HAUL: 'Short haul',
  LONG_HAUL: 'Long haul',
};

export const destinationPriorityLabel = (priority: number): string => `P${priority}`;

const DESTINATION_PRIORITY_LABEL: Record<number, string> = {
  1: destinationPriorityLabel(1),
  2: destinationPriorityLabel(2),
  3: destinationPriorityLabel(3),
};

export const formatDestinations = (
  destinations: ReadonlyArray<UserListItem['destinations'][number]>,
): string => {
  if (destinations.length === 0) return '';
  return destinations
    .map(
      (d) =>
        `${d.name} (${DESTINATION_PRIORITY_LABEL[d.priority] ?? destinationPriorityLabel(d.priority)})`,
    )
    .join(', ');
};

type CoverageSlice = Pick<
  UserListItem,
  'salesScopes' | 'salesHauls' | 'salesTripTypes' | 'destinations'
>;

/** Compact pills for the users grid. */
export const UserSalesCoverageCell = ({
  salesScopes,
  salesHauls,
  salesTripTypes,
  destinations,
}: CoverageSlice): React.JSX.Element => {
  const empty =
    salesScopes.length === 0 &&
    salesHauls.length === 0 &&
    salesTripTypes.length === 0 &&
    destinations.length === 0;

  if (empty) {
    return <span className="text-zinc-400">Not set</span>;
  }

  return (
    <div className="flex max-w-xs flex-col gap-1.5">
      {(salesScopes.length > 0 || salesHauls.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {salesScopes.map((s) => (
            <SoftPill key={s} className="bg-sky-50 text-sky-700">
              {SCOPE_LABELS[s]}
            </SoftPill>
          ))}
          {salesHauls.map((h) => (
            <SoftPill key={h} className="bg-amber-50 text-amber-800">
              {HAUL_LABELS[h]}
            </SoftPill>
          ))}
        </div>
      )}
      {salesTripTypes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {salesTripTypes.map((t) => (
            <SoftPill key={t.id} className="bg-violet-50 text-violet-700">
              {t.name}
            </SoftPill>
          ))}
        </div>
      )}
      {destinations.length > 0 && (
        <span className="text-xs text-zinc-600">{formatDestinations(destinations)}</span>
      )}
    </div>
  );
};

/** Read-only lines for the profile detail panel. */
export const UserSalesCoverageRows = ({
  salesScopes,
  salesHauls,
  salesTripTypes,
  destinations,
}: CoverageSlice): React.JSX.Element => {
  const scopeText =
    salesScopes.length > 0 ? salesScopes.map((s) => SCOPE_LABELS[s]).join(', ') : null;
  const haulText = salesHauls.length > 0 ? salesHauls.map((h) => HAUL_LABELS[h]).join(', ') : null;
  const tripText = salesTripTypes.length > 0 ? salesTripTypes.map((t) => t.name).join(', ') : null;
  const destText = formatDestinations(destinations) || null;

  return (
    <>
      <ProfileRow label="Sales scope" value={scopeText} />
      <ProfileRow label="Sales haul" value={haulText} />
      <ProfileRow label="Trip types" value={tripText} />
      <ProfileRow label="Destinations" value={destText} />
    </>
  );
};

export const ProfileRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element => (
  <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[160px_1fr] sm:gap-3">
    <div className="text-xs tracking-wider text-zinc-500 uppercase">{label}</div>
    <div className="text-sm text-zinc-900">
      {value === null || value === undefined || value === '' ? (
        <span className="text-zinc-400">-</span>
      ) : (
        value
      )}
    </div>
  </div>
);
